import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Sparkles, Loader2, Globe, Clock, Repeat, FileText,
  Zap, Calendar, Star, Play, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { ImageLightbox } from '../../components/ImageLightbox';

/**
 * Iter-174 — Client-facing AI Talent detail page with pricing.
 *
 * Backed by GET /api/ai-talent/{id}. Surfaces ALL pricing context the
 * client needs to make a booking decision: starter package, package
 * tiers, monthly retainer, licensing, revisions, turnaround.
 */
const fmtPrice = (n) => (n == null ? null : `$${Number(n).toLocaleString()}`);

const PriceCell = ({ label, value, sub }) => (
  <div className="flex flex-col gap-0.5">
    <p className="text-[11px] uppercase tracking-wide text-violet-300/70">{label}</p>
    <p className="text-2xl font-bold text-white leading-tight">{value || <span className="text-violet-300/50 text-base font-normal">—</span>}</p>
    {sub && <p className="text-[11px] text-violet-200/70">{sub}</p>}
  </div>
);

const ClientAIAvatarDetail = () => {
  const navigate = useNavigate();
  const { talentId } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/ai-talent/${talentId}`);
        if (!cancelled) setT(r.data);
      } catch (e) {
        toast.error('Failed to load AI talent');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [talentId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" /></div>;
  }
  if (!t) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card><CardContent className="py-10 text-center text-gray-500">AI talent not found.</CardContent></Card>
      </div>
    );
  }

  const tiers = Array.isArray(t.package_tiers) ? t.package_tiers : [];
  const startingPrice = t.base_package_price
    ?? (tiers.length ? Math.min(...tiers.map(x => Number(x.price)).filter(Number.isFinite)) : null);

  // Iter-225w — fix 404s: route to existing pages, not phantom paths.
  // Book Campaign → /client/book-ambassador/:id (AmbassadorBookingWizard)
  // Request Custom → /client/custom-ambassador?ai_talent_id=... (CustomAmbassadorRequest)
  const requestCampaign = () => navigate(`/client/book-ambassador/${talentId}`);
  const requestCustom = () => navigate(`/client/custom-ambassador?ai_talent_id=${talentId}`);

  // Combined, de-duplicated image set for the lightbox (hero first, then gallery).
  const heroSrc = t.avatar_url || t.profile_photo_url
    || (Array.isArray(t.gallery) && t.gallery[0]?.url) || null;
  const galleryArr = Array.isArray(t.gallery) ? t.gallery : [];
  const lightboxImages = [];
  const seen = new Set();
  if (heroSrc) { lightboxImages.push({ url: heroSrc, caption: `${t.first_name || ''} ${t.last_name || ''}`.trim() }); seen.add(heroSrc); }
  galleryArr.forEach((g) => { if (g?.url && !seen.has(g.url)) { lightboxImages.push({ url: g.url, caption: g.caption }); seen.add(g.url); } });
  const openLightboxByUrl = (url) => {
    const i = lightboxImages.findIndex((im) => im.url === url);
    setLightboxIdx(i >= 0 ? i : 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-violet-950/40 to-fuchsia-950/30" data-testid="client-ai-talent-detail">
      <header className="bg-zinc-950/60 backdrop-blur border-b border-violet-900/40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-violet-200 hover:text-white" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-400" /> AI Talent
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 grid lg:grid-cols-3 gap-6">
        {/* ── Left: avatar + meta ───────────────────────────────────── */}
        <div className="lg:col-span-1 space-y-4">
          {/* Iter-296c — Hero photo with fallback chain. Earlier this
              only checked `avatar_url`, which is NULL for the 12
              sample-talent showcase profiles whose photos live in
              `profile_photo_url` (and in the gallery). Falling back
              through avatar_url → profile_photo_url → first gallery
              image guarantees we never render a purple Sparkles
              placeholder when an image actually exists. */}
          <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-violet-800/40 to-fuchsia-800/40 border border-violet-900/40">
            {heroSrc
              ? <img src={heroSrc} alt={t.first_name}
                     onClick={() => openLightboxByUrl(heroSrc)}
                     className="w-full h-full object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                     data-testid="ai-avatar-image" />
              : <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-20 h-20 text-fuchsia-300/60" /></div>}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white" data-testid="ai-talent-name">
              {t.first_name} {t.last_name}
            </h2>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0 text-[10px]" data-testid="ai-avatar-identification">
                <Sparkles className="w-3 h-3 mr-0.5" /> AI Avatar · Virtual Talent
              </Badge>
              {t.style && <Badge variant="outline" className="border-violet-400/40 text-violet-200 text-[10px]">{t.style}</Badge>}
              {t.is_top_profile && (
                <Badge className="bg-amber-500/90 text-black border-0 text-[10px]">
                  <Star className="w-3 h-3 mr-0.5 fill-current" /> Top
                </Badge>
              )}
            </div>
          </div>
          {t.bio && <p className="text-sm text-violet-100/80 leading-relaxed">{t.bio}</p>}

          <div className="space-y-1.5 text-xs text-violet-200/80">
            {t.languages?.length > 0 && (
              <div className="flex items-center gap-2"><Globe className="w-3.5 h-3.5 shrink-0" /> {t.languages.join(', ')}</div>
            )}
            {t.industries?.length > 0 && (
              <div className="flex items-start gap-2"><Zap className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {t.industries.join(', ')}</div>
            )}
            {t.content_formats?.length > 0 && (
              <div className="flex items-start gap-2"><Play className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {t.content_formats.join(', ')}</div>
            )}
          </div>

          {t.demo_video_url && (
            <a href={t.demo_video_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 text-sm text-fuchsia-300 hover:text-fuchsia-200">
              <Play className="w-4 h-4" /> Watch demo reel
            </a>
          )}
        </div>

        {/* ── Right: pricing column ─────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Hero pricing card */}
          <Card className="bg-gradient-to-br from-violet-800/60 via-fuchsia-800/40 to-zinc-900 border-violet-700/40" data-testid="ai-pricing-card">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                <PriceCell
                  label="Starter package"
                  value={fmtPrice(startingPrice)}
                  sub={t.turnaround_days ? `${t.turnaround_days}-day delivery` : null}
                />
                <PriceCell
                  label="Monthly retainer"
                  value={fmtPrice(t.monthly_retainer_price)}
                  sub={t.monthly_retainer_price ? 'Ongoing campaign' : null}
                />
                <PriceCell
                  label="Revisions"
                  value={t.revision_limit != null ? `${t.revision_limit} included` : null}
                />
                <PriceCell
                  label="Turnaround"
                  value={t.turnaround_days ? `${t.turnaround_days} days` : null}
                  sub={t.turnaround_days ? 'Standard' : null}
                />
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button
                  onClick={requestCampaign}
                  className="bg-white text-violet-700 hover:bg-violet-50 font-semibold shadow-md"
                  data-testid="book-campaign-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" /> Book Campaign
                </Button>
                <Button
                  onClick={requestCustom}
                  variant="outline"
                  className="border-violet-400/50 text-violet-100 hover:bg-violet-800/40"
                  data-testid="custom-request-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> Request Custom
                </Button>
                {t.is_custom && (
                  <Button
                    onClick={() => navigate(`/client/avatar-studio/${talentId}`)}
                    className="bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-md"
                    data-testid="open-content-studio-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> Content Studio
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Package tiers */}
          {tiers.length > 0 && (
            <Card className="bg-zinc-900/80 border-zinc-800" data-testid="ai-package-tiers">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-fuchsia-400" /> Package Tiers
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tiers.map((tier, i) => (
                    <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-1.5"
                         data-testid={`ai-tier-${i}`}>
                      <p className="text-xs uppercase tracking-wide text-fuchsia-400/80">{tier.name || `Tier ${i + 1}`}</p>
                      <p className="text-2xl font-bold text-white">{fmtPrice(tier.price)}</p>
                      {tier.description && <p className="text-xs text-zinc-400 leading-relaxed">{tier.description}</p>}
                      {Array.isArray(tier.deliverables) && tier.deliverables.length > 0 && (
                        <ul className="text-[11px] text-zinc-300 space-y-0.5 pt-1">
                          {tier.deliverables.map((d, j) => <li key={j}>• {d}</li>)}
                        </ul>
                      )}
                      {tier.turnaround_days && (
                        <p className="text-[11px] text-zinc-500 pt-1"><Clock className="w-3 h-3 inline mr-0.5" />
                          {tier.turnaround_days}-day turnaround
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Iter-296b — Gallery section. Every AI profile shows at
              least one image (primary avatar fallback if no portfolio
              photos exist) per user spec: "no AI profile should open
              with missing images". */}
          {Array.isArray(t.gallery) && t.gallery.length > 0 && (
            <Card className="bg-zinc-900/80 border-zinc-800" data-testid="ai-gallery-card">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-fuchsia-400" /> Gallery
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="ai-gallery-grid">
                  {t.gallery.map((g, i) => (
                    <div
                      key={i}
                      onClick={() => openLightboxByUrl(g.url)}
                      className="relative aspect-square overflow-hidden rounded-lg bg-zinc-950 border border-zinc-800 cursor-zoom-in hover:opacity-90 transition-opacity"
                      data-testid={`ai-gallery-tile-${i}`}
                    >
                      <img
                        src={g.url}
                        alt={g.caption || `${t.first_name} gallery ${i + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Licensing & terms */}
          <Card className="bg-zinc-900/80 border-zinc-800">
            <CardContent className="p-5 grid sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs uppercase tracking-wide text-violet-300/70 mb-1.5 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Licensing & usage
                </h4>
                <p className="text-sm text-zinc-200 leading-relaxed" data-testid="ai-licensing-terms">
                  {t.licensing_terms || 'Standard 30-day campaign license included. Extended/perpetual licensing available on request.'}
                </p>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wide text-violet-300/70 mb-1.5 flex items-center gap-1.5">
                  <Repeat className="w-3.5 h-3.5" /> Revisions & turnaround
                </h4>
                <p className="text-sm text-zinc-200">
                  {t.revision_limit != null ? `${t.revision_limit} revision${t.revision_limit === 1 ? '' : 's'} included` : 'Revisions on request'}
                  {t.turnaround_days ? ` · ${t.turnaround_days} business-day delivery` : ''}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ImageLightbox
        images={lightboxImages}
        index={lightboxIdx}
        onIndexChange={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </div>
  );
};

export default ClientAIAvatarDetail;
